import { Plugin } from '@nuxt/types';
import { AxiosInstance } from 'axios';
import { createApiInstance } from '~/utils/api-factory';

/**
 * Report API Service Container Interface
 * Provides access to different report-related microservices
 *
 * @example this.$reportApi.default.get('/endpoint')
 * @example this.$reportApi.questionnaire.get('/dna-status/123')
 */
export interface ReportApiServices {
  /**
   * Default Service - General Report API endpoints
   * @example this.$reportApi.default.get('/endpoint')
   */
  default: AxiosInstance;

  /**
   * Questionnaire Service - Handles DNA questionnaire data
   * @example this.$reportApi.questionnaire.get('/dna-status/123')
   */
  questionnaire: AxiosInstance;
}

declare module 'vue/types/vue' {
  interface Vue {
    $reportApi: ReportApiServices;
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $reportApi: ReportApiServices;
  }
  interface Context {
    $reportApi: ReportApiServices;
  }
}

const reportApiPlugin: Plugin = (context, inject) => {
  // Service instance cache for lazy loading
  const serviceCache: Partial<Record<keyof ReportApiServices, AxiosInstance>> =
    {};

  const config = {
    reportUrl: context.$config.REPORT_URL || '<%= options.reportUrl %>',
    reportQuestionnaireUrl:
      context.$config.REPORT_QUESTIONNAIRE_URL ||
      '<%= options.reportQuestionnaireUrl %>'
  };
  /**
   * Lazy-loaded service container
   * Services are initialized on first access to save memory
   */
  const reportApi: ReportApiServices = {
    get default(): AxiosInstance {
      if (!serviceCache.default) {
        serviceCache.default = createApiInstance(config.reportUrl, context);
      }
      return serviceCache.default;
    },

    get questionnaire(): AxiosInstance {
      if (!serviceCache.questionnaire) {
        serviceCache.questionnaire = createApiInstance(
          config.reportQuestionnaireUrl,
          context
        );
      }
      return serviceCache.questionnaire;
    }
  };

  // Inject $reportApi into context
  inject('reportApi', reportApi);
};

export default reportApiPlugin;
